/**
 * AR Vision Demo — bootstrap the Scene 8 live-call config from the AR module
 * config. Demo on/off is toggled solely by the Cmd+K demo command; there is no
 * force-enable and no URL params.
 *
 * THROWAWAY DEMO CODE. Called once from the authenticated route's `beforeLoad`
 * (a single, clearly-commented line) so it resolves BEFORE the first AR page
 * renders — there is no flash of real backend data before the fixtures take
 * over.
 *
 * The demo settings live under the AR module config as an extra `demo` key:
 *
 *   config.ar.demo = {
 *     voiceConfigId: "<uuid>",   // Scene 8 live Vapi assistant + phone config
 *     callTo: "+1...",           // Scene 8 destination number (E.164)
 *   }
 *
 * This `demo` key is intentionally NOT part of the typed `ModuleConfig` schema
 * (it must delete cleanly with the demo). The schema is declared with
 * `.passthrough()`, so the value round-trips through reads/writes untouched and
 * we pick it out here via a narrow cast. Edit it from
 * Settings → Modules → (AR Invoicing) → edit JSON.
 *
 * Delete alongside frontend/src/demo/ar.
 */

import type { QueryClient } from '@tanstack/react-query';
import { ProductModule } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { setDemoLiveCallConfig } from './fixture-seed';
import { updateArDemoState } from './fixture-store';

interface ArDemoModuleSettings {
  voiceConfigId?: string | null;
  callTo?: string | null;
}

/** Resolve at most once per page load (ensureQueryData is also cached). */
let resolved = false;

export async function bootstrapArDemoFromModuleConfig(
  queryClient: QueryClient,
): Promise<void> {
  if (resolved) return;

  let demo: ArDemoModuleSettings | undefined;
  try {
    const configs = await queryClient.ensureQueryData(
      orpc.org.config.modules.getModuleConfigs.queryOptions(),
    );
    const arConfig = configs.find(
      (config) => config.module === ProductModule.AR_INVOICING,
    );
    // `demo` is a passthrough key not present in the typed schema (see header).
    const arSection = arConfig?.config.ar as
      | (Record<string, unknown> & { demo?: ArDemoModuleSettings })
      | undefined;
    demo = arSection?.demo;
  } catch {
    // No AR module config, module disabled, or the fetch failed — leave demo
    // mode as-is (URL param / session can still enable it for manual testing).
    // Allow a retry on the next navigation rather than latching `resolved`.
    return;
  }

  resolved = true;
  if (!demo) return;

  const voiceAgentConfigId = demo.voiceConfigId ?? null;
  const phoneNumber = demo.callTo ?? null;
  if (voiceAgentConfigId || phoneNumber) {
    // Set the holder so a one-click reset preserves it, then patch the store
    // that was already seeded (with no live config) at module load.
    setDemoLiveCallConfig({ voiceAgentConfigId, phoneNumber });
    updateArDemoState((draft) => {
      draft.voiceCall.liveVoiceAgentConfigId = voiceAgentConfigId;
      draft.voiceCall.livePhoneNumber = phoneNumber;
    });
  }
}
